import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, Filter, CheckCircle2, XCircle, AlertTriangle, ChevronDown, TrendingUp } from 'lucide-react';
import type { AssessmentResult } from '../hooks/useCalculator';

interface ProcessRecommendationsProps {
  result: AssessmentResult;
}

export default function ProcessRecommendations({ result }: ProcessRecommendationsProps) {
  const [filterType, setFilterType] = useState<'all' | 'Biological' | 'Physicochemical'>('all');
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const filtered = (result?.processCompatibility ?? []).filter(p => filterType === 'all' || p.type === filterType);

  const keyFindings = result?.keyFindings ?? [];
  const treatmentTrain = result?.treatmentTrain ?? [];

  const getScoreBarColor = (score: number) => {
    if (score >= 80) return 'bg-gradient-to-r from-emerald-500 to-emerald-400';
    if (score >= 60) return 'bg-gradient-to-r from-amber-500 to-yellow-400';
    return 'bg-gradient-to-r from-red-500 to-orange-400';
  };

  const getRecBadge = (rec: string) => {
    if (rec === 'RECOMMENDED') return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    if (rec === 'CONDITIONAL') return 'text-rb-brand bg-rb-brand/10 border-rb-brand/20';
    return 'text-red-400 bg-red-500/10 border-red-500/20';
  };

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h2 className="text-sm font-bold text-white">Process Recommendations</h2>
        <p className="text-xs text-slate-500">Treatment train and process compatibility scoring</p>
      </div>

      {/* Key Findings */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Key Findings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {keyFindings.map((finding, i) => (
            <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-800/50">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{finding.metric}</div>
              <div className="text-sm font-bold text-white mb-1">{finding.result}</div>
              <div className="text-xs text-slate-400">{finding.implication}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Treatment Train */}
      <div className="space-y-3">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Recommended Treatment Train</h3>
        <div className="space-y-2">
          {treatmentTrain.map((step, i) => (
            <div key={i} className="flex items-start gap-3 relative">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                {i + 1}
              </div>
              <div className="flex-1 p-3 rounded-lg bg-slate-800/50 border border-slate-800/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">{step.stage}</span>
                  <span className="text-[10px] text-slate-500">{step.scoreOrStatus}</span>
                </div>
                <div className="text-white font-medium text-sm">{step.process}</div>
                <div className="text-xs text-slate-500">{step.purpose}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Process Compatibility */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Treatment Process Compatibility</h3>
          <div className="flex gap-1.5">
            {(['all', 'Biological', 'Physicochemical'] as const).map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                  filterType === type
                    ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                    : 'bg-slate-800/50 text-slate-500 border-slate-700 hover:text-slate-300'
                }`}
              >
                {type === 'all' ? 'All' : type}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          {filtered.map((proc, i) => {
            const isExpanded = expandedIdx === i;
            return (
              <div
                key={i}
                className="p-3 rounded-xl bg-slate-800/30 border border-slate-800/50 hover:border-slate-600 transition-all cursor-pointer"
                onClick={() => setExpandedIdx(isExpanded ? null : i)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold shrink-0 ${proc.type === 'Biological' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'}`}>
                    {proc.compatibilityScore}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white">{proc.process}</div>
                    <div className="text-xs text-slate-500">{proc.type}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${getRecBadge(proc.recommendation)}`}>
                    {proc.recommendation}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>

                {/* Score bar */}
                <div className="mt-2.5 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${getScoreBarColor(proc.compatibilityScore)}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${proc.compatibilityScore}%` }}
                    transition={{ duration: 0.8, delay: i * 0.05 }}
                  />
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-3 pt-3 border-t border-slate-800/50 space-y-1.5">
                        <p className="text-xs text-slate-400"><strong className="text-white">Reason:</strong> {proc.reason}</p>
                        {proc.caveats && <p className="text-xs text-slate-400"><strong className="text-white">Caveats:</strong> {proc.caveats}</p>}
                        {proc.confidence && <p className="text-xs text-slate-400"><strong className="text-white">Confidence:</strong> {proc.confidence}</p>}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
